import React from 'react';

/**
 * Inline markdown in lesson strings: **bold** and *italic* (no extra dependency).
 * Matched delimiters are not shown as raw asterisks.
 */
export function parseInlineMarkdown(text) {
  if (text == null || typeof text !== 'string') return text;
  if (!text.includes('*')) return text;

  const out = [];
  const boldParts = text.split(/(\*\*[^*]+\*\*)/g);
  boldParts.forEach((segment, i) => {
    if (!segment) return;
    if (segment.startsWith('**') && segment.endsWith('**') && segment.length > 4) {
      out.push(
        <strong key={`b-${i}`} className="amm-num-edit-strong">
          {segment.slice(2, -2)}
        </strong>
      );
      return;
    }
    if (!segment.includes('*')) {
      out.push(segment);
      return;
    }
    const italicParts = segment.split(/(\*[^*]+\*)/g);
    italicParts.forEach((seg, j) => {
      if (!seg) return;
      if (seg.startsWith('*') && seg.endsWith('*') && seg.length > 2) {
        out.push(
          <em key={`e-${i}-${j}`} className="amm-num-edit-em">
            {seg.slice(1, -1)}
          </em>
        );
      } else {
        out.push(seg);
      }
    });
  });

  if (out.length === 0) return text;
  if (out.length === 1 && typeof out[0] === 'string') return out[0];
  return out;
}

/**
 * Split long paragraphs on sentence boundaries for readability (same words, more breaks).
 */
export function splitParagraphForReadability(text, maxSentencesPerChunk = 2) {
  if (!text || typeof text !== 'string') return [text];
  const trimmed = text.trim();
  const rough = trimmed.match(/[^.!?]+[.!?]+|[^.!?]+$/g);
  if (!rough || rough.length <= maxSentencesPerChunk) return [trimmed];

  const chunks = [];
  let buf = [];
  for (const s of rough) {
    const t = s.trim();
    if (!t) continue;
    buf.push(t);
    if (buf.length >= maxSentencesPerChunk) {
      chunks.push(buf.join(' '));
      buf = [];
    }
  }
  if (buf.length) chunks.push(buf.join(' '));
  return chunks.length ? chunks : [trimmed];
}

/**
 * Map lesson h2 labels to layout variants (visual hierarchy, no content edits).
 */
export function getLessonBlockVariant(h2) {
  if (!h2 || typeof h2 !== 'string') return 'default';
  const t = h2.toLowerCase().trim();
  if (t.startsWith('core idea')) return 'core';
  if (t.includes('calculation') || t.includes('step by step') || t.includes('step-by-step')) return 'calculation';
  if (t.includes('worked example') || t.includes('worked mini') || t.includes('worked numbers')) return 'example';
  if (
    t.includes('interpretation') ||
    t.includes('what it means') ||
    t.includes('how to read') ||
    t.includes('what it does in a reading')
  )
    return 'interpretation';
  if (
    t.includes('common mistake') ||
    t.includes('misreading') ||
    t.includes('misreadings') ||
    t.includes('checks and common')
  )
    return 'mistakes';
  if (t.startsWith('practice')) return 'practice';
  if (t.startsWith('next')) return 'next';
  if (t.includes('wider chart') || t.includes('in context') || t.includes('integration')) return 'context';
  if (t.includes('takeaway')) return 'takeaway';
  return 'default';
}
